import { useMemo, useState, type FunctionComponent } from "react";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Badge, Button, message, Popconfirm, Select, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { productsSettingsActions } from "@fitness-app/app-store";
import { type TPayIntegration } from "@fitness-app/data-models/entities/ProductsSettings";

import ModalForm from "~/components/ModalForm/ModalForm";
import { useLoading } from "~/hooks/useLoading";
import TPayIntegrationForm from "~/modules/Products/ProductsSettings/PaymentGateways/clients/tpay/TPayIntegrationForm";
import { type TPayIntegrationFormModel } from "~/modules/Products/ProductsSettings/PaymentGateways/clients/tpay/types";
import { useAppDispatch } from "~/store/initializeStore";

interface OwnProps {
  integrations: TPayIntegration[] | null;
}

type Props = OwnProps;

const ADD_NEW_ITEM = "ADD_NEW_ITEM";

const TPayIntegrationPanel: FunctionComponent<Props> = ({ integrations }) => {
  const { t } = useTranslation(["settings", "common", "dashboard"]);
  const [loading, startLoading, stopLoading] = useLoading();
  const [showModal, toggleModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAccountName, changeSelectedAccount] = useState<undefined | string>(undefined);
  const dispatch = useAppDispatch();

  const selectedAccount = useMemo(() => {
    return integrations?.find((item) => item.name === selectedAccountName);
  }, [selectedAccountName, integrations]);

  const handleSubmit = async (data: TPayIntegrationFormModel) => {
    startLoading();
    if (isEditing && selectedAccount) {
      await dispatch(
        productsSettingsActions.updateTPayIntegration({
          payload: {
            posId: selectedAccount.posId,
            name: data.name || selectedAccount.name,
            urlPrefix: data.urlPrefix ?? null,
            useTPayBlikRecurring: data.useTPayBlikRecurring ?? false,
          },
          onSuccess: () => {
            void message.success(t<string>("common:messages.success.default"));
            toggleModal(false);
            changeSelectedAccount(data.name || selectedAccount.name);
          },
          onFailure: (errorCode) => {
            if (errorCode === 403) {
              void message.error(t<string>("dashboard:tPayIntegration.accessError"));
              return;
            }
            void message.error(t<string>("dashboard:tPayIntegration.error"));
          },
        }),
      );
      stopLoading();
      return;
    }
    await dispatch(
      productsSettingsActions.integrateWithTPay({
        payload: { ...data, name: data.name || "TPay" },
        onSuccess: () => {
          void message.success(t<string>("dashboard:tPayIntegration.success"));
          toggleModal(false);
          handleChange(data.name);
        },
        onFailure: (errorCode) => {
          if (errorCode === 403) {
            void message.error(t<string>("dashboard:tPayIntegration.accessError"));
            return;
          }
          void message.error(t<string>("dashboard:tPayIntegration.error"));
        },
      }),
    );
    stopLoading();
  };

  const handleChange = (value?: string | undefined) => {
    if (value === ADD_NEW_ITEM || !value) {
      changeSelectedAccount(undefined);
      setIsEditing(false);
      toggleModal(true);
      return;
    }
    changeSelectedAccount(value);
  };

  const handleRemove = async () => {
    if (!selectedAccount) {
      return;
    }
    startLoading();
    await dispatch(
      productsSettingsActions.removeIntegrationWithTPay({
        payload: { posId: selectedAccount.posId, name: selectedAccount.name },
        onSuccess: () => {
          void message.success(t<string>("common:messages.success.default"));
          changeSelectedAccount(undefined);
        },
        onFailure: (errorCode) => {
          if (errorCode === 409) {
            void message.error(t<string>("settings:paymentGateway.tpay.removeConflict"));
            return;
          }
          void message.error(t<string>("dashboard:tPayIntegration.error"));
        },
      }),
    );
    stopLoading();
  };

  return (
    <Space direction="vertical" size={24}>
      {/* <div className="mx-auto flex justify-center">
        <img src={TPayIcon} alt={`tpay-icon`} className="h-14 self-center" />
      </div> */}

      <Typography.Title level={4}>{t("paymentGateway.tpay.connectedAccounts")}</Typography.Title>
      <Select
        size="large"
        placeholder={t("selectAccount")}
        style={{ minWidth: 600 }}
        notFoundContent={false}
        onChange={handleChange}
        onSelect={handleChange}
        value={selectedAccount?.name}
        optionFilterProp="title"
        optionLabelProp="title"
      >
        {(integrations || []).map((account) => (
          <Select.Option value={account.name} title={account.name} key={account.name}>
            {account.name}
          </Select.Option>
        ))}
        {
          <Select.Option value={ADD_NEW_ITEM} title={t<string>("addNewAccount")}>
            <Button
              type="link"
              style={{
                flex: "none",
                padding: "8px",
                display: "block",
                cursor: "pointer",
              }}
            >
              <PlusOutlined /> {t("addNewAccount")}
            </Button>
          </Select.Option>
        }
      </Select>
      {selectedAccount && (
        <>
          <Space direction="horizontal" align="end" style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              type="dashed"
              icon={<EditOutlined />}
              onClick={() => {
                setIsEditing(true);
                toggleModal(true);
              }}
            >
              {t("common:button.edit")}
            </Button>
            <Popconfirm
              onConfirm={() => void handleRemove()}
              title={t("paymentGateway.tpay.deleteWarning")}
              okText={t("common:button.delete")}
              okButtonProps={{ danger: true, loading }}
            >
              <Button type="dashed" danger icon={<DeleteOutlined />}>
                {t("common:button.delete")}
              </Button>
            </Popconfirm>
          </Space>
          <div
            style={{
              maxWidth: 600,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Space size={16} direction="vertical">
              <Typography.Title level={5} style={{ marginBottom: 0 }}>
                {t<string>("settings:clientIntegration.status")}
              </Typography.Title>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t<string>("settings:paymentGateway.tpay.clientId")}: <strong>{selectedAccount.clientId}</strong>
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t<string>("settings:paymentGateway.tpay.posId")}: <strong>{selectedAccount.posId}</strong>
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t<string>("settings:paymentGateway.tpay.urlPrefix")}:{" "}
                <strong>{selectedAccount.urlPrefix || "—"}</strong>
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t<string>("settings:paymentGateway.tpay.useTPayBlikRecurring")}:{" "}
                {selectedAccount.useTPayBlikRecurring ? (
                  <CheckCircleOutlined className="text-green-500" />
                ) : (
                  <CloseCircleOutlined className="text-red-500" />
                )}
              </Typography.Paragraph>
              <Badge status="success" text={t<string>("settings:clientIntegration.completed")} />
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t<string>("settings:paymentGateway.tpay.default")}:{" "}
                {selectedAccount.isDefault ? (
                  <CheckCircleOutlined className="text-green-500" />
                ) : (
                  <CloseCircleOutlined className="text-red-500" />
                )}
              </Typography.Paragraph>
            </Space>
          </div>
        </>
      )}

      <ModalForm
        open={showModal}
        loading={loading}
        onCancel={() => {
          toggleModal(false);
          setIsEditing(false);
        }}
        title={
          isEditing && selectedAccount ? t("paymentGateway.tpay.editAccount") : t("paymentGateway.tpay.addNewAccount")
        }
      >
        <TPayIntegrationForm
          onSubmit={handleSubmit}
          editMode={isEditing && !!selectedAccount}
          model={
            isEditing && selectedAccount
              ? {
                  name: selectedAccount.name,
                  posId: selectedAccount.posId,
                  clientId: selectedAccount.clientId,
                  clientSecret: "",
                  safetyKey: "",
                  urlPrefix: selectedAccount.urlPrefix ?? "",
                  useTPayBlikRecurring: selectedAccount.useTPayBlikRecurring ?? false,
                }
              : undefined
          }
        />
      </ModalForm>
    </Space>
  );
};

export default TPayIntegrationPanel;
